import { TrashIcon } from '@phosphor-icons/react'
import { ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  BookingCategory,
  BookingQuestion,
  BookingType,
  useDeleteBookingCategory,
  useDeleteBookingQuestion,
  useDeleteBookingType,
} from '@/api/booking-types'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { popupFactory, toastFactory } from '@/factories'
import { getApiErrorMessage } from '@/utils/error'

export const useBookingDelete = () => {
  const { t } = useTranslation(['organisation', 'common'])
  const { mutateAsync: deleteGroup } = useDeleteBookingCategory()
  const { mutateAsync: deleteType } = useDeleteBookingType()
  const { mutateAsync: deleteQuestion } = useDeleteBookingQuestion()

  const confirmDelete = ({
    body,
    delete: remove,
    deletedMessage,
    title,
  }: {
    body: ReactNode
    delete: () => Promise<unknown>
    deletedMessage: string
    title: string
  }) =>
    popupFactory.open(ConfirmationPopup, {
      children: body,
      confirmIcon: TrashIcon,
      confirmText: t('common:actions.delete'),
      onConfirm: async () => {
        try {
          await remove()
          toastFactory.success(deletedMessage)
        } catch (error) {
          toastFactory.error(getApiErrorMessage(error))
        }
      },
      title,
      variant: 'danger',
    })

  const handleDeleteGroup = (group: BookingCategory, typesCount = 0) =>
    confirmDelete({
      body: (
        <>
          <Trans
            components={[<strong key="name" />]}
            i18nKey="organisation:bookingTypes.group.deleteConfirm.body"
            t={t}
            values={{ name: group.name }}
          />
          {typesCount > 0 && (
            <>
              <br />
              <br />
              {t('organisation:bookingTypes.group.deleteConfirm.typesWarning', {
                count: typesCount,
              })}
            </>
          )}
        </>
      ),
      delete: () => deleteGroup(group.id),
      deletedMessage: t('organisation:bookingTypes.group.toasts.deleted'),
      title: t('organisation:bookingTypes.group.deleteConfirm.title'),
    })

  const handleDeleteType = (bookingType: BookingType, questionsCount = 0) =>
    confirmDelete({
      body: (
        <>
          <Trans
            components={[<strong key="name" />]}
            i18nKey="organisation:bookingTypes.type.deleteConfirm.body"
            t={t}
            values={{ name: bookingType.name }}
          />
          {questionsCount > 0 && (
            <>
              <br />
              <br />
              {t('organisation:bookingTypes.type.deleteConfirm.questionsWarning', {
                count: questionsCount,
              })}
            </>
          )}
        </>
      ),
      delete: () => deleteType(bookingType.id),
      deletedMessage: t('organisation:bookingTypes.type.toasts.deleted'),
      title: t('organisation:bookingTypes.type.deleteConfirm.title'),
    })

  const handleDeleteQuestion = (question: BookingQuestion) =>
    confirmDelete({
      body: (
        <Trans
          components={[<strong key="title" />]}
          i18nKey="organisation:bookingTypes.question.deleteConfirm.body"
          t={t}
          values={{ title: question.title }}
        />
      ),
      delete: () => deleteQuestion(question.id),
      deletedMessage: t('organisation:bookingTypes.question.toasts.deleted'),
      title: t('organisation:bookingTypes.question.deleteConfirm.title'),
    })

  return { handleDeleteGroup, handleDeleteQuestion, handleDeleteType }
}
